import { CommonModule } from "@angular/common";
import { ChangeDetectorRef, Component, ElementRef, HostListener, OnDestroy, OnInit } from "@angular/core";
import { FormsModule } from "@angular/forms";
import { DomSanitizer, SafeResourceUrl, SafeUrl } from "@angular/platform-browser";
import { Observable, firstValueFrom } from "rxjs";
import Swal from "sweetalert2";
import { FirmaFrcComponent } from "../vacaciones/firma-frc/firma-frc.component";
import { PinAutorizadorService } from "../security/pin-autorizador/pin-autorizador.service";
import { PinAutorizadorRes } from "../shared/model/security/pin-autorizador.model";

import { EvaluacionesIaService } from "./evaluaciones-ia.service";
import { MatrizRiesgoService } from "../matriz-riesgos-ia/matriz-riesgo.service";
import { MatrizEvidencia } from "../shared/model/iso42001/matriz-riesgo.model";
import {
  DICTAMEN_TEXTO,
  DictamenEval,
  EvalCatalogo,
  EvalCriterio,
  EvalDocumentoCat,
  EvalDominio,
  EvalHistorial,
  EvalProveedor,
  EvalProveedorRes,
  EvalResultado,
  ORIGEN_EVAL_PROVEEDOR_DOC,
  RIESGO_TEXTO,
  RespuestaEval,
  RiesgoEval,
} from "../shared/model/iso42001/evaluacion-proveedor.model";

type Vista = "lista" | "evaluacion" | "expediente" | "resultado";
type EstadoDoc = "CUMPLE" | "NO_CUMPLE" | "SIN";

interface FilaProveedor {
  id: number;
  nombre: string;
  servicio: string;
  ini: string;
  color: string;
  avance: number;
  puntaje: string;
  puntajeNota: string;
  riesgo: RiesgoEval;
  dictamen: string;
  firmada: boolean;
}

interface Kpi {
  label: string;
  val: number;
  sub: string;
  tono: "neutro" | "ok" | "warn" | "apagado";
}

/**
 * Una sección en el panel de la izquierda. La barra es lo que lleva ganado
 * de su peso (Sí cumple entre sus preguntas), no cuántas van respondidas:
 * de un vistazo se ve cómo va cada 33.3 %.
 */
interface NavDominio {
  id: string;
  num: number;
  nombre: string;
  peso: string;
  hechos: number;
  total: number;
  cumplen: number;
  color: string;
  /** 0-100: la parte del peso que ya ganó. */
  score: number;
  /** "27.8" de "33.3". */
  aporteTexto: string;
  /** "1 sin responder" mientras falte algo; vacío con la sección completa. */
  nota: string;
}

/** Lo que se edita desde la tabla: los datos del proveedor y su bitácora. */
interface Edicion {
  id: number;
  nombre: string;
  firmada: boolean;
  datos: {
    nombre: string;
    casoUso: string;
    razonSocial: string;
    servicio: string;
    planModalidad: string;
    fechaEvaluacion: string;
  };
}

interface PreguntaVista {
  c: EvalCriterio;
  respuesta: RespuestaEval | null;
  observacion: string;
  editando: boolean;
  borrador: string;
  guardando: boolean;
}

interface DocVista {
  cat: EvalDocumentoCat;
  idEvalDoc: number | null;
  criterio: string;
  estado: EstadoDoc;
  referencia: string;
  /** Lo tecleado en "dónde consta" que todavía no se guarda: no se pisa al refrescar. */
  sucia: boolean;
  archivos: MatrizEvidencia[];
  subiendo: boolean;
  /** En Sí cumple y sin archivo: así no se puede aprobar (dónde consta no lo sustituye). */
  sinRespaldo: boolean;
  /** "Dónde consta" que no se pudo guardar: se avisa y se reintenta al salir del campo. */
  sinGuardar: boolean;
}

/** El tooltip con la vista previa de un archivo del expediente. */
interface Vistazo {
  a: MatrizEvidencia;
  tipo: "imagen" | "pdf" | "otro";
  estado: "cargando" | "listo" | "error" | "grande" | "sin";
  urlImagen: SafeUrl | null;
  urlPdf: SafeResourceUrl | null;
  meta: string;
  x: number;
  y: number;
  arriba: boolean;
  ancho: number;
}

/** Un renglón de la tabla "Puntaje por sección", ya con sus textos. */
interface DominioVista {
  id: string;
  nombre: string;
  peso: string;
  hechos: number;
  total: number;
  score: number | null;
  /** Color de la barra: gris mientras la sección no esté contestada completa. */
  nivel: string;
  scoreTexto: string;
  aporteTexto: string;
}

interface Requisito {
  texto: string;
  ok: boolean;
  valor: string;
}

interface Pendiente {
  /** De qué proveedor es: la fila sigue aunque se abra otro. */
  idEval: number;
  op: () => Observable<EvalProveedorRes>;
  porOmision: string;
  /** Al terminar esa operación, bien o mal, y antes de pintar. */
  alTerminar?: (bien: boolean, res?: EvalProveedorRes) => void;
}

const LLAVE_LOCAL = "cos.evaluaciones-ia.v1";
const FUENTES_ID = "eia-fuentes";
const FUENTES_URL =
  "https://fonts.googleapis.com/css2?family=Bricolage+Grotesque:wght@500;600;700&family=Public+Sans:wght@400;500;600;700&display=swap";

const ACCION_TEXTO: Record<string, string> = {
  ALTA: "Alta",
  DATOS: "Datos",
  RESPUESTA: "Respuesta",
  // "Marcar/Limpiar dominio", hasta el 15/09/2026: quedan renglones viejos.
  DOMINIO: "Dominio",
  LIMPIEZA: "Limpieza",
  OBSERVACION: "Observación",
  REFERENCIA: "Dónde consta",
  ARCHIVO: "Archivo",
  FIRMA: "Firma",
  REAPERTURA: "Reapertura",
};

/** "OpenAI — ChatGPT" → "OC", igual que el prototipo. */
function iniciales(nombre?: string | null): string {
  const palabras = String(nombre || "?")
    .split(/[\s—–-]+/)
    .filter((x) => /[A-Za-z0-9]/.test(x));
  return (palabras.slice(0, 2).map((x) => x[0]).join("") || "?").toUpperCase();
}

/** Hoy en la zona de quien mira, para el tope del campo de fecha. */
function isoHoy(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

/** "2026-09-14 10:22" → "14/09/2026". */
function fechaCorta(f?: string | null): string {
  if (!f) {
    return "—";
  }
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(f);
  return m ? `${m[3]}/${m[2]}/${m[1]}` : f;
}

/** "Certificado.PDF" → "pdf". */
/** "88.9", "100" o "—". Un decimal, como lo manda el servidor. */
function texto1(n?: number | null): string {
  if (n == null) {
    return "—";
  }
  const v = Math.round(n * 10) / 10;
  return Number.isInteger(v) ? String(v) : v.toFixed(1);
}

function extension(nombre?: string | null): string {
  const n = String(nombre || "");
  const i = n.lastIndexOf(".");
  return i < 0 ? "" : n.slice(i + 1).toLowerCase();
}

function tamanoLegible(bytes: number): string {
  if (bytes < 1024) {
    return `${bytes} B`;
  }
  if (bytes < 1024 * 1024) {
    return `${Math.round(bytes / 1024)} KB`;
  }
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

/** La fecha de carga llega como número, texto o fecha, según cómo la serialice el servidor. */
function fechaHora(valor?: Date | string | number | null): string {
  if (valor == null || valor === "") {
    return "";
  }
  const d = valor instanceof Date ? valor : typeof valor === "number" ? new Date(valor) : new Date(String(valor).replace(" ", "T"));
  if (Number.isNaN(d.getTime())) {
    return "";
  }
  const dos = (n: number) => String(n).padStart(2, "0");
  return `${dos(d.getDate())}/${dos(d.getMonth() + 1)}/${d.getFullYear()} ${dos(d.getHours())}:${dos(d.getMinutes())}`;
}

function escapa(t?: string | null): string {
  const mapa: Record<string, string> = { "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" };
  return String(t ?? "").replace(/[&<>"']/g, (c) => mapa[c]);
}

/**
 * Evaluaciones IA Solser: una sola pantalla con las tres pestañas del
 * diseño (Codigo_02/crear modulo/): Proveedores, Cuestionario y
 * expediente, y Resultado.
 *
 * TODO lo que es regla (puntaje, riesgo, si se puede aprobar) viene
 * calculado del servidor. Aquí sólo se pinta: cada respuesta vuelve con
 * el expediente recalculado.
 *
 * Las respuestas van EN FILA, una detrás de otra. Con cinco clics rápidos
 * en paralelo, las respuestas del servidor pueden llegar desordenadas y
 * la pantalla acabaría enseñando un estado viejo de algo que sí se
 * guardó. En fila, sólo se pinta la última.
 */
@Component({
  selector: "app-evaluaciones-ia",
  standalone: true,
  imports: [CommonModule, FormsModule, FirmaFrcComponent],
  templateUrl: "./evaluaciones-ia.component.html",
  styleUrls: ["./evaluaciones-ia.component.css"],
})
export class EvaluacionesIaComponent implements OnInit, OnDestroy {
  readonly EMPRESA = "SOLSER";
  /** La línea de estado de la barra, según la pestaña (sin firmar). */
  get notaVista(): string {
    switch (this.vista) {
      case "evaluacion":
        return "Llena los datos del proveedor y responde con Sí o No cumple; se guarda solo.";
      case "expediente":
        return "La evidencia documental de lo que está en Sí cumple.";
      case "resultado":
        return "Revisa el puntaje, el riesgo y los requisitos; el dictamen se firma abajo.";
      default:
        return "";
    }
  }

  /**
   * Lo que falta para ver el resultado: el cuestionario terminado, los
   * datos del proveedor y el archivo de cada pregunta en Sí cumple (si no
   * hay ninguna en Sí cumple, no se pide evidencia). Una evaluación firmada
   * siempre se puede ver. Pedido de Norman, 23/09/2026: «que no aparezca el
   * botón para ver resultado hasta que esté lleno el cuestionario y el
   * expediente».
   */
  get faltaParaResultado(): string[] {
    if (this.firmada) {
      return [];
    }
    // Mientras llega el detalle, lo calculado es del proveedor anterior.
    if (!this.sel || !this.res) {
      return ["abrir el expediente"];
    }
    const falta: string[] = [];
    if (!this.expedienteHabilitado) {
      falta.push("terminar el cuestionario");
    }
    if (this.faltanDatos.length) {
      falta.push("completar los datos del proveedor (en el cuestionario)");
    }
    const sinArchivo = this.evidenciaRequerida - this.evidenciaConArchivo;
    if (this.expedienteHabilitado && sinArchivo > 0) {
      falta.push(sinArchivo === 1 ? "subir la evidencia de 1 documento" : `subir la evidencia de ${sinArchivo} documentos`);
    }
    return falta;
  }

  /**
   * Los tres requisitos para ver el resultado, para la tarjeta del
   * expediente: cada uno con su palomita o su tache.
   */
  get requisitosResultado(): Array<{ texto: string; ok: boolean }> {
    const evidencia = !this.expedienteHabilitado
      ? "Evidencia"
      : this.evidenciaRequerida
        ? `Evidencia ${this.evidenciaConArchivo} de ${this.evidenciaRequerida}`
        : "Evidencia (no aplica)";
    return [
      { texto: "Cuestionario terminado", ok: this.expedienteHabilitado },
      { texto: "Datos del proveedor", ok: !this.faltanDatos.length },
      { texto: evidencia, ok: this.expedienteHabilitado && this.evidenciaConArchivo === this.evidenciaRequerida },
    ];
  }

  get puedeVerResultado(): boolean {
    return this.faltaParaResultado.length === 0;
  }

  /** «completar los datos del proveedor y subir la evidencia de 2 documentos». */
  get faltaResultadoTexto(): string {
    const f = this.faltaParaResultado;
    return f.length <= 1 ? f.join("") : f.slice(0, -1).join(", ") + " y " + f[f.length - 1];
  }

  /**
   * Al restaurar la pestaña de la visita anterior: si era el Resultado y
   * ya no se puede ver (por ejemplo, se reabrió y falta algo), se cae al
   * expediente al llegar el detalle.
   */
  private revisaResultado = false;

  /**
   * El nombre de la barra: el del detalle o, mientras llega (o si falló),
   * el de la fila de la lista, para que la barra no cambie de título ni
   * de alto al abrir un proveedor.
   */
  get nombreBarra(): string {
    if (this.sel) {
      return this.sel.nombre || "";
    }
    const e = this.evaluaciones.find((x) => x.idEvalProveedor === this.idSel);
    return e ? e.nombre || "" : "";
  }

  /** «Paso 3 · Expediente»: lo que dice la barra arriba del nombre. */
  get pasoTexto(): string {
    const t = this.TABS.find((x) => x.id === this.vista);
    return t ? `Paso ${t.n} · ${t.label}` : "";
  }

  /** Las pestañas; el número va en su círculo, aparte del nombre. */
  readonly TABS: Array<{ id: Vista; n: number; label: string }> = [
    { id: "lista", n: 1, label: "Proveedores" },
    { id: "evaluacion", n: 2, label: "Cuestionario" },
    { id: "expediente", n: 3, label: "Expediente" },
    { id: "resultado", n: 4, label: "Resultado" },
  ];
  readonly RIESGO_TEXTO = RIESGO_TEXTO;
  readonly hoy = isoHoy();

  /** Los colores del diseño: avatares rotan, cada dominio tiene el suyo. */
  private readonly COLORES = [
    "oklch(0.52 0.13 40)",
    "oklch(0.52 0.11 155)",
    "oklch(0.52 0.13 300)",
    "oklch(0.52 0.13 350)",
    "oklch(0.52 0.09 220)",
    "oklch(0.52 0.1 110)",
    "oklch(0.52 0.12 260)",
    "oklch(0.45 0.02 260)",
  ];
  private readonly DOM_COLOR: Record<string, string> = {
    SEG: "oklch(0.48 0.12 245)",
    DATOS: "oklch(0.48 0.13 320)",
    // Era verde azulado; ámbar desde el 23/09 (sin verdes fuera de «Sí cumple»).
    OPER: "oklch(0.5 0.11 65)",
  };

  vista: Vista = "lista";
  catalogo: EvalCatalogo | null = null;
  totalCriterios = 13;
  totalDominios = 3;

  /* ---------------- 1 · Proveedores ---------------- */
  evaluaciones: EvalProveedor[] = [];
  filas: FilaProveedor[] = [];
  kpis: Kpi[] = [];
  cargandoLista = true;
  errorLista = "";
  nuevoAbierto = false;
  nuevoNombre = "";
  nuevoCaso = "";
  creando = false;

  /**
   * La tabla va de cinco en cinco, sin scroll. La lista llega completa
   * (los KPIs cuentan todo); aquí sólo se corta la página que se ve.
   */
  readonly POR_PAGINA = 5;
  pagina = 1;
  totalPaginas = 1;
  filasPagina: FilaProveedor[] = [];
  /** Hasta cinco números de página alrededor de la actual. */
  paginas: number[] = [];
  rangoTexto = "";

  /* ---------------- 2 · Cuestionario y 3 · Expediente ---------------- */
  idSel: number | null = null;
  sel: EvalProveedor | null = null;
  res: EvalResultado | null = null;
  firmada = false;
  cargandoDetalle = false;
  errorDetalle = "";
  iniSel = "";
  colorSel = "";

  datos = { razonSocial: "", servicio: "", planModalidad: "", fechaEvaluacion: "" };
  textoGuardado = "";

  dom = "SEG";
  domNum = 1;
  domActual: EvalDominio | null = null;
  navDom: NavDominio[] = [];
  preguntas: PreguntaVista[] = [];

  docs: DocVista[] = [];
  docsResumen = "";
  /** Respondidas en pantalla: van por delante del servidor mientras la fila trabaja. */
  respondidasLocal = 0;
  /** El expediente se habilita al terminar el cuestionario (las 13 respondidas). */
  expedienteHabilitado = false;
  expedienteAviso = "";
  /** En Sí cumple, cuántos documentos piden archivo y cuántos ya lo tienen. */
  evidenciaRequerida = 0;
  evidenciaConArchivo = 0;
  pesoActual = "";
  /** Los documentos que impiden aprobar por no tener archivo ni dónde consta. */
  sinRespaldo: string[] = [];

  puntajeTexto = "—";
  dictamenActual = "";

  /* ---------------- 3 · Resultado ---------------- */
  requisitos: Requisito[] = [];
  dominiosVista: DominioVista[] = [];
  /** Datos del proveedor obligatorios que todavía no están guardados. */
  faltanDatos: string[] = [];
  riesgoNota = "";
  veredicto = "";
  veredictoNota = "";
  veredictoTono: "ok" | "mal" | "neutro" = "neutro";
  /** Lo que dice la caja Dictamen del resultado (paso 4): el firmado si lo hay, si no el sugerido. */
  dictamenCaja = "";
  firmaNota = "";
  dictamenFirmado = "";
  fechaFirmaTexto = "—";

  dictamenElegido: DictamenEval | null = null;
  /** Si quien evalúa escogió a mano. Si no, el radio sigue al sugerido. */
  private dictamenManual = false;
  firmando = false;
  reabriendo = false;
  /** El modal de la firma digital, al cerrar y firmar. */
  firmaAbierta = false;
  firmaLeyenda = "";
  private firmaPara: { id: number; dictamen: DictamenEval } | null = null;
  /** Ya se pulsó Cancelar: se sale en cuanto termine lo que va en camino. */
  saliendo = false;
  private salidaDesde = 0;
  private relojSalida: ReturnType<typeof setTimeout> | null = null;
  /** Lo que no va por la fila: los datos del proveedor y quitar un archivo. */
  private guardandoDatos = 0;
  private quitando = 0;

  /* ---------------- Editar desde la tabla ---------------- */
  edicion: Edicion | null = null;

  /**
   * Pestaña abierta dentro del modal de edición. Los datos y la bitácora ya no
   * van uno debajo del otro: la bitácora crece con cada cambio —hay expedientes
   * con más de 30 líneas— y empujaba los campos fuera de la vista, así que para
   * editar había que rodar hacia arriba cada vez.
   */
  tabEdicion: "datos" | "bitacora" = "datos";
  guardandoEdicion = false;
  /** Lo que se dice al pie del cuadro: error, aviso o "guardado". */
  edicionError = "";
  edicionTono: "error" | "aviso" | "ok" = "error";
  private relojEdicion: ReturnType<typeof setTimeout> | null = null;
  /** El lápiz que abrió el cuadro: al cerrar, el foco vuelve ahí. */
  private edicionDesde: HTMLElement | null = null;
  /** La bitácora del expediente que se está editando (o consultando, si está firmado). */
  historial: EvalHistorial[] = [];
  cargandoHistorial = false;
  historialError = "";

  /** Cada petición de detalle lleva su número: sólo se pinta la más nueva. */
  private seq = 0;
  private pendientes: Pendiente[] = [];
  /** Los documentos que se están subiendo, por id: ver marcaSubiendo(). */
  private subiendoDocs = new Set<number>();
  /** "idEval|claveDoc" de las referencias que no se guardaron. */
  private refsSinGuardar = new Set<string>();
  private enCurso = false;
  private relojGuardado: ReturnType<typeof setTimeout> | null = null;

  /** El tooltip con la vista previa de un archivo; uno a la vez. */
  vistazo: Vistazo | null = null;
  private relojVistazo: ReturnType<typeof setTimeout> | null = null;
  private relojCierre: ReturnType<typeof setTimeout> | null = null;
  private cerradoEn = 0;
  /** Lo ya bajado para la vista previa, por id de evidencia: un archivo no cambia, se reusa. */
  private blobsVistazo = new Map<number, string>();
  /** Más grande que esto no se baja sólo para asomarse. */
  private readonly MAX_VISTAZO = 15 * 1024 * 1024;

  constructor(
    private service: EvaluacionesIaService,
    private evidencias: MatrizRiesgoService,
    private cdr: ChangeDetectorRef,
    private host: ElementRef<HTMLElement>,
    private sanitizer: DomSanitizer,
    private pinService: PinAutorizadorService,
  ) {}

  ngOnInit(): void {
    this.cargaFuentes();
    // En captura: la página se desplaza dentro de .main-panel, no en window.
    document.addEventListener("scroll", this.alDesplazar, true);
    const previo = this.leeLocal();
    if (previo && typeof previo.dom === "string") {
      this.dom = previo.dom;
    }
    this.cargarLista(() => {
      const id = previo && typeof previo.idSel === "number" ? previo.idSel : null;
      if (id && this.evaluaciones.some((e) => e.idEvalProveedor === id)) {
        const v: Vista =
          previo.vista === "resultado" || previo.vista === "evaluacion" || previo.vista === "expediente"
            ? previo.vista
            : "lista";
        this.revisaResultado = v === "resultado";
        this.abrir(id, v, false);
      }
    });
  }

  ngOnDestroy(): void {
    if (this.relojSalida) {
      clearTimeout(this.relojSalida);
    }
    if (this.relojGuardado) {
      clearTimeout(this.relojGuardado);
    }
    document.removeEventListener("scroll", this.alDesplazar, true);
    this.cierraVistazo(true);
    this.blobsVistazo.forEach((url) => URL.revokeObjectURL(url));
    this.blobsVistazo.clear();
  }

  /* ======================= Pestañas ======================= */

  irVista(v: Vista): void {
    if (v !== "lista" && !this.idSel) {
      return;
    }
    // Al resultado sólo con todo lleno (el botón y la pestaña ya lo
    // impiden; esto cubre cualquier otro camino).
    if (v === "resultado" && this.vista !== "resultado" && !this.puedeVerResultado) {
      return;
    }
    // Al dejar el cuestionario, la observación que se estaba escribiendo
    // se guarda, como al cambiar de sección: la pestaña se va de la
    // pantalla y el borrador no debe quedarse a medias.
    if (this.vista === "evaluacion" && v !== "evaluacion") {
      for (const p of this.preguntas) {
        if (p.editando && !p.guardando) {
          this.guardarObs(p);
        }
      }
    }
    this.cierraVistazo(true);
    this.vista = v;
    this.guardaLocal();
    this.cdr.detectChanges();
    this.subeAlInicio();
  }

  /**
   * Con un proveedor abierto la pestaña de Proveedores se apaga: se sale con
   * Cancelar o firmando en Resultado. Lo respondido ya está guardado, o va
   * en la fila, que termina sola; aquí sólo se cierra el expediente.
   */
  cancelar(): void {
    if (this.vista === "lista" || this.saliendo || this.firmando || this.reabriendo) {
      return;
    }
    // Lo que se está escribiendo en una observación también se guarda: sin
    // esto, el borrador se perdía al salir sin pulsar su «Guardar».
    for (const p of this.preguntas) {
      if (p.editando && !p.guardando) {
        this.guardarObs(p);
      }
    }
    // Y lo que no se pudo guardar antes se vuelve a intentar: los datos del
    // proveedor (sólo se mandan si difieren de lo guardado) y «dónde consta».
    this.guardarDatos();
    for (const d of this.docs) {
      if (d.sucia || d.sinGuardar) {
        this.guardarReferencia(d);
      }
    }
    this.saliendo = true;
    this.salidaDesde = Date.now();
    this.cdr.detectChanges();
    this.saleCuandoTermine();
  }

  /**
   * Algo de este expediente sigue en camino: la fila (respuestas,
   * observaciones, dónde consta, refrescos), una subida, los datos del
   * proveedor, quitar un archivo, la firma o la reapertura.
   */
  /** Lo que se ve en los datos del proveedor no es lo que está guardado. */
  get datosSinGuardar(): boolean {
    const ev = this.sel;
    if (!ev || this.firmada) {
      return false;
    }
    return (
      this.datos.razonSocial.trim() !== (ev.razonSocial || "") ||
      this.datos.servicio.trim() !== (ev.servicio || "") ||
      this.datos.planModalidad.trim() !== (ev.planModalidad || "") ||
      (this.datos.fechaEvaluacion || "") !== (ev.fechaEvaluacion || "")
    );
  }

  get ocupado(): boolean {
    return (
      this.enCurso ||
      this.pendientes.length > 0 ||
      this.subiendoDocs.size > 0 ||
      this.guardandoDatos > 0 ||
      this.quitando > 0 ||
      this.firmando ||
      this.reabriendo
    );
  }

  /**
   * Sale cuando ya no hay nada en camino. Si algo falla mientras tanto, quien
   * lo falla apaga «saliendo» y se queda en el expediente, con su aviso: así
   * no se pierde un error en la tabla ni un reintento sin su texto.
   */
  private saleCuandoTermine(): void {
    this.relojSalida = null;
    if (!this.saliendo) {
      return;
    }
    if (!this.ocupado) {
      this.volverALista();
      return;
    }
    if (Date.now() - this.salidaDesde > 20000) {
      this.saliendo = false;
      this.aviso("Todavía se está guardando: espera un momento y vuelve a pulsar Cancelar.", "warning");
      this.cdr.detectChanges();
      return;
    }
    this.relojSalida = setTimeout(() => this.saleCuandoTermine(), 150);
  }

  private volverALista(): void {
    this.saliendo = false;
    if (this.relojSalida) {
      clearTimeout(this.relojSalida);
      this.relojSalida = null;
    }
    this.cierraVistazo(true);
    this.firmaAbierta = false;
    this.firmaPara = null;
    this.idSel = null;
    this.sel = null;
    this.res = null;
    this.firmada = false;
    this.preguntas = [];
    this.docs = [];
    this.dictamenManual = false;
    this.dictamenElegido = null;
    this.errorDetalle = "";
    this.vista = "lista";
    this.guardaLocal();
    // La tabla ya se fue poniendo al día con cada respuesta. Con la fila
    // vacía se vuelve a pedir, por lo que hayan cambiado los demás; con
    // algo en camino no, para que una lista vieja no pise lo que llega.
    if (!this.enCurso && !this.pendientes.length) {
      this.cargarLista();
    }
    this.cdr.detectChanges();
    this.subeAlInicio();
  }

  /* ======================= 1 · Proveedores ======================= */

  cargarLista(luego?: () => void): void {
    this.cargandoLista = true;
    this.errorLista = "";
    this.service.lista().subscribe({
      next: (res) => {
        this.cargandoLista = false;
        if (res.codigoError) {
          this.errorLista = this.mensaje(res, "No se pudo cargar la lista de proveedores.");
          this.cdr.detectChanges();
          return;
        }
        if (res.catalogo) {
          this.catalogo = res.catalogo;
        }
        this.evaluaciones = res.evaluaciones || [];
        this.armaLista();
        this.cdr.detectChanges();
        if (luego) {
          luego();
        }
      },
      error: () => {
        this.cargandoLista = false;
        this.errorLista = "No se pudo conectar con el servidor.";
        this.cdr.detectChanges();
      },
    });
  }

  abrirNuevo(): void {
    this.nuevoAbierto = true;
    this.cdr.detectChanges();
    setTimeout(() => this.host.nativeElement.querySelector<HTMLInputElement>("#eia-nuevo-nombre")?.focus());
  }

  cerrarNuevo(): void {
    this.nuevoAbierto = false;
    this.nuevoNombre = "";
    this.nuevoCaso = "";
  }

  crear(): void {
    const nombre = this.nuevoNombre.trim();
    const caso = this.nuevoCaso.trim();
    if (!nombre || !caso || this.creando) {
      return;
    }
    this.creando = true;
    this.service.crear(nombre, caso).subscribe({
      next: (res) => {
        this.creando = false;
        if (res.codigoError || !res.idGenerado) {
          this.aviso(this.mensaje(res, "No se pudo crear el expediente."), "error");
          this.cdr.detectChanges();
          return;
        }
        const id = res.idGenerado;
        this.cerrarNuevo();
        this.cargarLista(() => this.abrir(id, "evaluacion"));
      },
      error: () => {
        this.creando = false;
        this.aviso("No se pudo conectar con el servidor.", "error");
        this.cdr.detectChanges();
      },
    });
  }

  abrir(id: number, vista: Vista = "evaluacion", sube = true): void {
    if (this.idSel !== id) {
      // El icono de la barra, de la fila de la lista, desde ya.
      const fila = this.evaluaciones.find((e) => e.idEvalProveedor === id);
      this.iniSel = iniciales(fila ? fila.nombre : "");
      this.colorSel = this.colorDe(id);
      this.sel = null;
      this.res = null;
      this.preguntas = [];
      this.docs = [];
      this.dictamenManual = false;
      this.dictamenElegido = null;
      // La fila NO se vacía: cada operación sabe de qué proveedor es, y lo
      // que ya se enseñó como hecho tiene que llegar al servidor.
    }
    this.cierraVistazo(true);
    this.idSel = id;
    this.vista = vista;
    // Al volver a la tabla se ve la página del proveedor que se abrió.
    const i = this.filas.findIndex((f) => f.id === id);
    if (i >= 0) {
      this.pagina = Math.floor(i / this.POR_PAGINA) + 1;
      this.ajustaPagina();
    }
    this.guardaLocal();
    this.cargarDetalle(id);
    if (sube) {
      this.subeAlInicio();
    }
  }

  /**
   * Elimina un expediente creado por error (pedido de Norman, 15/09/2026).
   * Pide el PIN personal de autorización de quien elimina (el de Ajustes) y
   * lo valida el servidor; si no es, avisa dentro del mismo cuadro para
   * volver a escribirlo. Antes revisa que lo tenga definido y que no esté
   * bloqueado. Las firmadas no traen el botón.
   */
  eliminar(f: FilaProveedor): void {
    if (f.firmada || Swal.isVisible()) {
      return;
    }
    this.pinService.estado().subscribe({
      next: (pin) => {
        if (pin.codigoError) {
          this.aviso(this.mensajePin(pin, "No se pudo revisar tu PIN de autorización."), "error");
        } else if (!pin.tienePin) {
          this.sinPinParaEliminar();
        } else if (pin.bloqueadoHasta) {
          Swal.fire({
            icon: "warning",
            title: "Tu PIN está bloqueado",
            text: `Se bloqueó por intentos fallidos, hasta las ${pin.bloqueadoHasta}. Vuelve a intentarlo después.`,
            confirmButtonText: "Entendido",
            confirmButtonColor: "#0e7a6d",
          });
        } else {
          this.confirmaEliminar(f);
        }
      },
      error: () => this.aviso("No se pudo revisar tu PIN: sin conexión con el servidor.", "error"),
    });
  }

  /** Sin PIN no se elimina: se define una sola vez en Ajustes, con la contraseña de COS. */
  private sinPinParaEliminar(): void {
    Swal.fire({
      icon: "info",
      title: "Aún no defines tu PIN de autorización",
      text:
        "Eliminar un expediente pide tu PIN personal. Se define una sola vez en Ajustes, con tu contraseña de COS; " +
        "al terminar, vuelve aquí e intenta de nuevo.",
      showCancelButton: true,
      confirmButtonText: "Definir mi PIN",
      confirmButtonColor: "#0e7a6d",
      cancelButtonText: "Cancelar",
    }).then((x) => {
      if (x.isConfirmed) {
        // En otra pestaña, como Homologación: esta lista se queda donde estaba.
        window.open("#/admin/ajustes", "_blank", "noopener");
      }
    });
  }

  private confirmaEliminar(f: FilaProveedor): void {
    Swal.fire({
      title: "¿Eliminar el expediente?",
      html:
        `<p style="margin:0"><strong>${escapa(f.nombre)}</strong></p>` +
        `<p style="margin:.6em 0 0;font-size:.92em">Se borran sus respuestas, observaciones, archivos y bitácora. No se puede deshacer.</p>`,
      icon: "warning",
      input: "password",
      inputLabel: "Tu PIN de autorización",
      inputAttributes: {
        maxlength: "8",
        inputmode: "numeric",
        // Como en Ajustes y Homologación: que el navegador no lo guarde como contraseña.
        autocomplete: "one-time-code",
        "aria-label": "Tu PIN de autorización",
      },
      showCancelButton: true,
      confirmButtonText: "Eliminar",
      cancelButtonText: "Cancelar",
      confirmButtonColor: "#b3261e",
      showLoaderOnConfirm: true,
      allowOutsideClick: () => !Swal.isLoading(),
      inputValidator: (v) => (/^\d{6,8}$/.test(String(v || "").trim()) ? null : "Tu PIN va de 6 a 8 dígitos."),
      preConfirm: (pin: string) =>
        firstValueFrom(this.service.eliminar(f.id, String(pin || "").trim()))
          .then((res) => {
            if (res.codigoError) {
              // El PIN equivocado no se queda escrito en el cuadro.
              const campo = Swal.getInput();
              if (campo) {
                campo.value = "";
              }
              Swal.showValidationMessage(this.mensaje(res, "No se pudo eliminar el expediente."));
              return false;
            }
            return true;
          })
          .catch(() => {
            Swal.showValidationMessage("No se pudo eliminar: sin conexión con el servidor.");
            return false;
          }),
    }).then((x) => {
      if (!x.isConfirmed) {
        return;
      }
      // El título del toast es HTML: el nombre lo escribió alguien.
      this.aviso(`Se eliminó «${escapa(f.nombre)}».`, "success");
      this.evaluaciones = this.evaluaciones.filter((e) => e.idEvalProveedor !== f.id);
      this.armaLista();
      this.cdr.detectChanges();
      this.cargarLista();
    });
  }

  private armaLista(): void {
    if (this.catalogo) {
      this.totalDominios = this.catalogo.dominios.length;
      this.totalCriterios = this.catalogo.dominios.reduce((a, d) => a + d.criterios.length, 0);
    }
    this.filas = this.evaluaciones
      .filter((e) => !!e.resultado && e.idEvalProveedor != null)
      .map((e) => {
        const r = e.resultado as EvalResultado;
        const firmada = e.estatus === "FIRMADA";
        let dictamen: string;
        if (firmada && e.dictamen) {
          dictamen = DICTAMEN_TEXTO[e.dictamen] + " · firmada";
        } else if (r.respondidos) {
          // Sin "Sugerido:" (pedido de Norman, 15/09/2026): es Aprobado o No aprobado.
          // Lo firmado se distingue por el "· firmada" de arriba.
          dictamen = r.sugerido ? DICTAMEN_TEXTO[r.sugerido] + " · sin firmar" : "Sin concluir";
        } else {
          dictamen = "No iniciada";
        }
        return {
          id: e.idEvalProveedor as number,
          nombre: e.nombre || "",
          servicio: e.servicio || e.casoUso || "",
          ini: iniciales(e.nombre),
          color: this.colorDe(e.idEvalProveedor as number),
          avance: r.avance,
          // Firmada a medias (Rechazado): ese puntaje es el firmado, no provisional.
          puntaje: r.concluida || (firmada && r.respondidos) ? texto1(r.total) : "—",
          puntajeNota: r.concluida || firmada ? "" : r.respondidos ? "prov. " + texto1(r.total) : "",
          riesgo: r.riesgo,
          dictamen,
          firmada,
        };
      });

    const aprobadas = this.evaluaciones.filter((e) => e.estatus === "FIRMADA" && e.dictamen === "APROBADO").length;
    const incompletas = this.evaluaciones.filter((e) => (e.resultado ? e.resultado.avance : 0) < 100).length;
    this.kpis = [
      { label: "Proveedores evaluados", val: this.evaluaciones.length, sub: "expedientes en el registro", tono: "neutro" },
      { label: "Uso autorizado", val: aprobadas, sub: "aprobados y firmados", tono: "ok" },
      { label: "Evaluación incompleta", val: incompletas, sub: "faltan criterios por responder", tono: incompletas ? "warn" : "apagado" },
    ];
    this.ajustaPagina();
  }

  irPagina(n: number): void {
    if (n < 1 || n > this.totalPaginas || n === this.pagina) {
      return;
    }
    this.pagina = n;
    this.ajustaPagina();
    this.cdr.detectChanges();
  }

  /** Recorta la página actual. Si la lista se achicó, no se queda en una página vacía. */
  private ajustaPagina(): void {
    this.totalPaginas = Math.max(1, Math.ceil(this.filas.length / this.POR_PAGINA));
    this.pagina = Math.min(Math.max(1, this.pagina), this.totalPaginas);
    const inicio = (this.pagina - 1) * this.POR_PAGINA;
    this.filasPagina = this.filas.slice(inicio, inicio + this.POR_PAGINA);
    this.rangoTexto = this.filas.length ? `${inicio + 1}–${inicio + this.filasPagina.length} de ${this.filas.length}` : "";
    let desde = Math.max(1, this.pagina - 2);
    const hasta = Math.min(this.totalPaginas, desde + 4);
    desde = Math.max(1, hasta - 4);
    this.paginas = [];
    for (let n = desde; n <= hasta; n++) {
      this.paginas.push(n);
    }
  }

  /* ======================= 2 · Detalle ======================= */

  /**
   * copiaDatos en false para los refrescos que no pidió quien mira (al
   * terminar una subida, al quitar un archivo, cuando falla la fila): así
   * no le borran lo que está escribiendo en los datos del proveedor.
   */
  private cargarDetalle(id: number, luego?: () => void, copiaDatos = true): void {
    this.cargandoDetalle = true;
    this.errorDetalle = "";
    const mio = ++this.seq;
    this.service.detalle(id).subscribe({
      next: (res) => {
        if (mio !== this.seq || this.idSel !== id) {
          return;
        }
        this.cargandoDetalle = false;
        if (res.codigoError || !res.evaluacion) {
          this.falloDetalle(this.mensaje(res, "No se pudo abrir el expediente."));
          return;
        }
        if (res.catalogo) {
          this.catalogo = res.catalogo;
        }
        this.aplica(res.evaluacion, copiaDatos);
        if (this.revisaResultado) {
          this.revisaResultado = false;
          if (this.vista === "resultado" && !this.puedeVerResultado) {
            this.vista = "expediente";
            this.guardaLocal();
            this.cdr.detectChanges();
          }
        }
        if (luego) {
          luego();
        }
      },
      error: () => {
        if (mio !== this.seq || this.idSel !== id) {
          return;
        }
        this.cargandoDetalle = false;
        this.falloDetalle("No se pudo conectar con el servidor.");
      },
    });
  }

  /**
   * Sin expediente pintado, el aviso va en la página, con Reintentar. Con el
   * expediente ya pintado va en un toast: antes un refresco que fallaba
   * (o una sesión caducada) pasaba en silencio y se veían datos viejos.
   */
  private falloDetalle(texto: string): void {
    this.errorDetalle = texto;
    if (this.sel && this.res) {
      this.aviso(texto + " Lo que ves puede no estar al día.", "error");
    }
    this.cdr.detectChanges();
  }

  reintentarDetalle(): void {
    if (this.idSel) {
      this.cargarDetalle(this.idSel);
    }
  }

  /**
   * Pinta un expediente que llegó del servidor.
   *
   * copiaDatos en false cuando llega por una respuesta: los cuatro campos
   * del proveedor se guardan aparte, y copiarlos aquí le borraría a quien
   * está escribiendo lo que todavía no sale del campo.
   */
  private aplica(ev: EvalProveedor, copiaDatos: boolean): void {
    const otro = !this.sel || this.sel.idEvalProveedor !== ev.idEvalProveedor;
    this.sel = ev;
    this.res = ev.resultado || null;
    this.firmada = ev.estatus === "FIRMADA";
    if (copiaDatos || otro || this.firmada) {
      this.datos = {
        razonSocial: ev.razonSocial || "",
        servicio: ev.servicio || "",
        planModalidad: ev.planModalidad || "",
        fechaEvaluacion: ev.fechaEvaluacion || "",
      };
    }
    this.actualizaFila(ev);
    this.armaDetalle();
    this.cdr.detectChanges();
  }

  /** La fila de la tabla se queda al día sin volver a pedir la lista. */
  private actualizaFila(ev: EvalProveedor): void {
    const i = this.evaluaciones.findIndex((e) => e.idEvalProveedor === ev.idEvalProveedor);
    if (i >= 0) {
      this.evaluaciones[i] = { ...this.evaluaciones[i], ...ev, respuestas: undefined, documentos: undefined };
    }
    this.armaLista();
  }

  private armaDetalle(): void {
    const ev = this.sel;
    const cat = this.catalogo;
    const r = this.res;
    if (!ev || !cat || !r || !cat.dominios.length) {
      return;
    }
    const respuestas = new Map((ev.respuestas || []).map((x) => [x.clave, x]));
    this.iniSel = iniciales(ev.nombre);
    this.colorSel = this.colorDe(ev.idEvalProveedor as number);

    this.navDom = cat.dominios.map((d, i) => {
      const pd = r.dominios.find((x) => x.id === d.id);
      const hechos = pd ? pd.hechos : 0;
      const total = d.criterios.length;
      const cumplen = pd ? pd.cumplen : 0;
      const faltan = total - hechos;
      return {
        id: d.id,
        num: i + 1,
        nombre: d.nombre,
        peso: texto1(d.peso),
        hechos,
        total,
        cumplen,
        color: this.colorDom(d.id),
        score: pd && pd.score != null ? pd.score : 0,
        aporteTexto: pd && pd.aporte != null ? texto1(pd.aporte) : "0",
        nota: !hechos ? "Sin responder" : faltan ? `${faltan} sin responder` : "",
      };
    });

    let i = cat.dominios.findIndex((d) => d.id === this.dom);
    if (i < 0) {
      i = 0;
      this.dom = cat.dominios[0].id;
    }
    this.domActual = cat.dominios[i];
    this.domNum = i + 1;
    this.pesoActual = texto1(this.domActual.peso);

    const previas = new Map(this.preguntas.map((p) => [p.c.clave, p]));
    this.preguntas = this.domActual.criterios.map((c) => {
      const x = respuestas.get(c.clave);
      const previa = previas.get(c.clave);
      const observacion = (x && x.observacion) || "";
      const editando = !!previa && previa.editando && !this.firmada;
      return {
        c,
        respuesta: (x && x.respuesta) || null,
        observacion,
        editando,
        borrador: editando && previa ? previa.borrador : observacion,
        guardando: !!previa && previa.guardando,
      };
    });

    const porClave = new Map((ev.documentos || []).map((d) => [d.claveDoc, d]));
    const docsPrevios = new Map(this.docs.map((d) => [d.cat.clave, d]));
    this.docs = cat.documentos.map((dc) => {
      const d = porClave.get(dc.clave);
      const rc = respuestas.get(dc.criterio);
      const previo = docsPrevios.get(dc.clave);
      const estado: EstadoDoc = rc && rc.respuesta === "CUMPLE" ? "CUMPLE" : rc && rc.respuesta === "NO_CUMPLE" ? "NO_CUMPLE" : "SIN";
      const sucia = !!previo && previo.sucia && !this.firmada;
      const archivos = (d && d.archivos) || [];
      return {
        cat: dc,
        idEvalDoc: d ? d.idEvalDoc : null,
        criterio: this.etiquetaCriterio(dc.criterio),
        estado,
        referencia: sucia && previo ? previo.referencia : (d && d.referencia) || "",
        sucia,
        archivos,
        subiendo: !!d && this.subiendoDocs.has(d.idEvalDoc),
        sinGuardar: !this.firmada && this.refsSinGuardar.has(ev.idEvalProveedor + "|" + dc.clave),
        // En Sí cumple el archivo es obligatorio para aprobar; "dónde consta" no lo sustituye.
        sinRespaldo: estado === "CUMPLE" && !archivos.length,
      };
    });
    this.sinRespaldo = this.docs.filter((x) => x.sinRespaldo).map((x) => x.cat.nombre);
    const enCumple = this.docs.filter((d) => d.estado === "CUMPLE");
    this.evidenciaRequerida = enCumple.length;
    this.evidenciaConArchivo = enCumple.filter((d) => d.archivos.length).length;
    this.docsResumen = `${enCumple.length} en Sí cumple · ${this.evidenciaConArchivo} con archivo`;

    const claves = cat.dominios.flatMap((d) => d.criterios.map((c) => c.clave));
    this.respondidasLocal = claves.filter((k) => !!(respuestas.get(k) || {}).respuesta).length;
    const faltanLocal = claves.length - this.respondidasLocal;
    // Lo que se ve Y lo que ya confirmó el servidor: el servidor no deja subir
    // ni anotar mientras falte una respuesta por guardarse.
    this.expedienteHabilitado = this.firmada || (faltanLocal === 0 && r.concluida);
    this.expedienteAviso =
      faltanLocal === 0
        ? "Guardando la última respuesta…"
        : faltanLocal === 1
          ? "Falta 1 pregunta por responder."
          : `Faltan ${faltanLocal} preguntas por responder.`;

    this.puntajeTexto = r.respondidos ? texto1(r.total) : "—";
    if (this.firmada && ev.dictamen) {
      this.dictamenActual = DICTAMEN_TEXTO[ev.dictamen] + " · firmada";
    } else {
      this.dictamenActual = r.sugerido ? DICTAMEN_TEXTO[r.sugerido] + " · sin firmar" : "Sin concluir";
    }

    // Los dos primeros deciden si CUMPLE (sólo el cuestionario). Los otros
    // dos no suman puntos: se piden para firmar.
    const faltanDatos = this.datosFaltantes();
    this.requisitos = [
      { texto: "Todas las preguntas respondidas", ok: r.concluida, valor: `${r.respondidos} de ${r.totalCriterios}` },
      {
        texto: `Puntaje del cuestionario de ${r.umbral} o más`,
        ok: r.respondidos > 0 && r.total >= r.umbral,
        valor: r.respondidos ? `${texto1(r.total)} de 100` : "—",
      },
      {
        texto: "Evidencia subida en cada documento en Sí cumple",
        ok: this.evidenciaConArchivo === this.evidenciaRequerida,
        valor: `${this.evidenciaConArchivo} de ${this.evidenciaRequerida}`,
      },
      {
        texto: "Datos del proveedor completos",
        ok: !faltanDatos.length,
        valor: faltanDatos.length ? `faltan ${faltanDatos.length}` : "completos",
      },
    ];
    this.dominiosVista = r.dominios.map((d) => ({
      id: d.id,
      nombre: d.nombre,
      peso: texto1(d.peso),
      hechos: d.hechos,
      total: d.total,
      score: d.score,
      // A medias el puntaje de la sección cuenta lo pendiente en 0: no se pinta de rojo ni de verde.
      nivel: d.hechos < d.total ? "nulo" : this.nivelScore(d.score),
      scoreTexto: texto1(d.score),
      aporteTexto: texto1(d.aporte),
    }));

    if (!r.respondidos) {
      this.riesgoNota = "Cuestionario sin responder.";
    } else if (!r.concluida) {
      this.riesgoNota = "Evaluación incompleta.";
    } else if (!r.puedeAprobar) {
      this.riesgoNota = `No autorizar: el puntaje no llega a ${r.umbral}.`;
    } else {
      this.riesgoNota =
        r.riesgo === "BAJO"
          ? "Uso autorizado; reevaluar cada 24 meses."
          : "Uso autorizado con seguimiento de los hallazgos; reevaluar en 12 meses.";
    }
    this.veredicto = r.respondidos ? (r.puedeAprobar ? "CUMPLE" : "NO CUMPLE") : "SIN EVALUAR";
    this.veredictoTono = r.respondidos ? (r.puedeAprobar ? "ok" : "mal") : "neutro";
    if (r.puedeAprobar) {
      const pendiente = [
        this.sinRespaldo.length
          ? `subir la evidencia de ${this.sinRespaldo.length === 1 ? "un documento" : this.sinRespaldo.length + " documentos"}`
          : "",
        faltanDatos.length ? "completar los datos del proveedor" : "",
      ].filter(Boolean);
      this.veredictoNota = pendiente.length
        ? `El cuestionario cumple (${texto1(r.total)} de 100); para aprobar falta ${pendiente.join(" y ")}.`
        : `El cuestionario cumple (${texto1(r.total)} de 100): puede aprobarse.`;
    } else if (!r.concluida) {
      this.veredictoNota = r.faltan === 1 ? "Falta 1 pregunta por responder." : `Faltan ${r.faltan} preguntas por responder.`;
    } else {
      this.veredictoNota = `El puntaje del cuestionario es ${texto1(r.total)} y para cumplir se necesitan ${r.umbral}.`;
    }
    // Firmada: lo que dice es SU dictamen y SU vigencia, no lo que sugiere el cálculo.
    this.dictamenCaja = this.firmada && ev.dictamen
      ? DICTAMEN_TEXTO[ev.dictamen] + " · firmada"
      : r.sugerido
        ? DICTAMEN_TEXTO[r.sugerido] + " · sin firmar"
        : "Sin concluir";

    if (this.firmada) {
      this.dictamenElegido = ev.dictamen || null;
      this.dictamenManual = false;
    } else if (!this.dictamenManual) {
      this.dictamenElegido = r.sugerido;
    } else if (this.dictamenElegido === "APROBADO" && !r.puedeAprobar) {
      // Como en el diseño: un Aprobado que deja de cumplir se muestra como No aprobado.
      this.dictamenElegido = "RECHAZADO";
    }
    this.dictamenFirmado = ev.dictamen ? DICTAMEN_TEXTO[ev.dictamen] : "—";
    this.fechaFirmaTexto = ev.fechaFirma ? `${fechaCorta(ev.fechaFirma)} ${ev.fechaFirma.slice(11, 16)}`.trim() : "—";
    this.faltanDatos = faltanDatos;
    this.firmaNota = this.firmada
      ? `Firmada el ${fechaCorta(ev.fechaFirma)}${ev.firmo ? " por " + ev.firmo : ""}`
      : faltanDatos.length
        ? "Para firmar faltan los datos del proveedor: " + faltanDatos.join(", ") + "."
        : r.puedeAprobar
          ? this.sinRespaldo.length
            ? "Para firmar como Aprobado falta subir la evidencia de: " + this.sinRespaldo.join(", ") + "."
            : "Sin firmar"
          : "Solo puede firmarse como No aprobado hasta que el cuestionario cumpla.";
  }

  /** Lo obligatorio de los datos del proveedor que todavía no está guardado. */
  private datosFaltantes(): string[] {
    const ev = this.sel;
    if (!ev) {
      return [];
    }
    const faltan: string[] = [];
    if (!(ev.razonSocial || "").trim()) {
      faltan.push("razón social");
    }
    if (!(ev.servicio || "").trim()) {
      faltan.push("servicio contratado");
    }
    if (!(ev.planModalidad || "").trim()) {
      faltan.push("plan / modalidad");
    }
    if (!(ev.fechaEvaluacion || "").trim()) {
      faltan.push("fecha de evaluación");
    }
    return faltan;
  }

  /* ---------------- Datos del proveedor ---------------- */

  guardarDatos(): void {
    const ev = this.sel;
    if (!ev || this.firmada) {
      return;
    }
    const id = ev.idEvalProveedor as number;
    const d = {
      razonSocial: this.datos.razonSocial.trim(),
      servicio: this.datos.servicio.trim(),
      planModalidad: this.datos.planModalidad.trim(),
      fechaEvaluacion: this.datos.fechaEvaluacion || "",
    };
    if (
      d.razonSocial === (ev.razonSocial || "") &&
      d.servicio === (ev.servicio || "") &&
      d.planModalidad === (ev.planModalidad || "") &&
      d.fechaEvaluacion === (ev.fechaEvaluacion || "")
    ) {
      return;
    }
    this.marcaGuardado("Guardando…");
    this.guardandoDatos++;
    this.service.guardarDatos(id, d).subscribe({
      next: (res) => {
        this.guardandoDatos--;
        if (res.codigoError) {
          this.saliendo = false;
          this.marcaGuardado("");
          this.aviso(this.mensaje(res, "No se guardaron los datos del proveedor."), "error");
          return;
        }
        const actual = this.evaluaciones.find((e) => e.idEvalProveedor === id);
        for (const obj of [this.sel && this.sel.idEvalProveedor === id ? this.sel : null, actual]) {
          if (obj) {
            obj.razonSocial = d.razonSocial;
            obj.servicio = d.servicio;
            obj.planModalidad = d.planModalidad;
            obj.fechaEvaluacion = d.fechaEvaluacion;
          }
        }
        this.armaLista();
        // Pone al día el requisito "Datos del proveedor completos".
        this.armaDetalle();
        this.marcaGuardado("Guardado", 2500);
      },
      error: () => {
        this.guardandoDatos--;
        this.saliendo = false;
        this.marcaGuardado("");
        this.aviso("No se guardaron los datos del proveedor: sin conexión con el servidor.", "error");
      },
    });
  }

  private marcaGuardado(texto: string, borraEn?: number): void {
    if (this.relojGuardado) {
      clearTimeout(this.relojGuardado);
      this.relojGuardado = null;
    }
    this.textoGuardado = texto;
    this.cdr.detectChanges();
    if (borraEn) {
      this.relojGuardado = setTimeout(() => {
        this.textoGuardado = "";
        this.cdr.detectChanges();
      }, borraEn);
    }
  }

  /* ---------------- Cuestionario ---------------- */

  elegirDominio(id: string): void {
    if (this.dom === id) {
      return;
    }
    // Las preguntas del dominio se rehacen: un borrador abierto se guarda antes.
    for (const p of this.preguntas) {
      if (p.editando && !p.guardando) {
        this.guardarObs(p);
      }
    }
    this.dom = id;
    this.preguntas = [];
    this.armaDetalle();
    this.guardaLocal();
    this.cdr.detectChanges();
  }

  domAnterior(): void {
    const doms = this.catalogo ? this.catalogo.dominios : [];
    const i = doms.findIndex((d) => d.id === this.dom);
    if (i > 0) {
      this.elegirDominio(doms[i - 1].id);
    }
  }

  domSiguiente(): void {
    const doms = this.catalogo ? this.catalogo.dominios : [];
    const i = doms.findIndex((d) => d.id === this.dom);
    if (i >= 0 && i < doms.length - 1) {
      this.elegirDominio(doms[i + 1].id);
      this.subeAlPanel();
    } else if (i === doms.length - 1) {
      this.terminarCuestionario();
    }
  }

  /**
   * «Terminar» en la última sección: pasa a la pestaña del expediente. Sólo
   * con todo respondido y confirmado por el servidor (expedienteHabilitado),
   * que es cuando el expediente deja subir evidencia.
   */
  terminarCuestionario(): void {
    if (!this.puedeTerminar) {
      return;
    }
    this.irVista("expediente");
  }

  /**
   * «Terminar» pide el cuestionario completo: todas las respuestas (y
   * confirmadas por el servidor) y los datos del proveedor, que desde el
   * 23/09/2026 van en esta pestaña. Los datos se miran en lo que está
   * escrito en pantalla, así el botón se prende en cuanto se llenan.
   */
  get puedeTerminar(): boolean {
    return this.firmada || (this.expedienteHabilitado && !this.datosQueFaltan.length);
  }

  /** Por qué «Terminar» está apagado, al lado del botón. */
  get avisoTerminar(): string {
    if (!this.expedienteHabilitado) {
      return this.expedienteAviso;
    }
    const f = this.datosQueFaltan;
    return f.length ? "Faltan los datos del proveedor: " + f.join(", ") + "." : "";
  }

  /** Los datos del proveedor vacíos en lo que se ve (no en lo guardado). */
  private get datosQueFaltan(): string[] {
    const d = this.datos;
    const f: string[] = [];
    if (!(d.razonSocial || "").trim()) {
      f.push("razón social");
    }
    if (!(d.servicio || "").trim()) {
      f.push("servicio contratado");
    }
    if (!(d.planModalidad || "").trim()) {
      f.push("plan / modalidad");
    }
    if (!d.fechaEvaluacion) {
      f.push("fecha de evaluación");
    }
    return f;
  }

  responder(p: PreguntaVista, valor: RespuestaEval): void {
    const ev = this.sel;
    if (!ev || this.firmada || p.respuesta === valor) {
      return;
    }
    const id = ev.idEvalProveedor as number;
    const clave = p.c.clave;
    this.ponLocal([clave], valor);
    this.encola(id, () => this.service.responder(id, clave, valor), "No se guardó la respuesta.");
  }

  /**
   * Borra TODAS las respuestas y vuelve a la primera pregunta. Las
   * observaciones y los archivos del expediente se quedan.
   */
  limpiarCuestionario(): void {
    const ev = this.sel;
    const cat = this.catalogo;
    if (!ev || !cat || this.firmada || !this.respondidasLocal) {
      return;
    }
    const id = ev.idEvalProveedor as number;
    const contestadas = this.respondidasLocal;
    Swal.fire({
      title: "¿Limpiar el cuestionario?",
      text: `Se ${contestadas === 1 ? "borra la respuesta" : "borran las " + contestadas + " respuestas"} de las ${this.totalCriterios} preguntas. Las observaciones y los archivos del expediente se quedan.`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Limpiar",
      cancelButtonText: "Cancelar",
      confirmButtonColor: "#b3261e",
    }).then((r) => {
      if (!r.isConfirmed || this.idSel !== id || this.firmada) {
        return;
      }
      this.ponLocal(cat.dominios.flatMap((d) => d.criterios.map((c) => c.clave)), null);
      this.encola(id, () => this.service.limpiarCuestionario(id), "No se pudo limpiar el cuestionario.");
      this.elegirDominio(cat.dominios[0].id);
    });
  }

  /** Lo que se ve cambia al instante; los números llegan con la respuesta del servidor. */
  private ponLocal(claves: string[], valor: RespuestaEval | null): void {
    const ev = this.sel;
    if (!ev) {
      return;
    }
    const lista = ev.respuestas || (ev.respuestas = []);
    for (const clave of claves) {
      const x = lista.find((r) => r.clave === clave);
      if (x) {
        x.respuesta = valor;
      } else {
        lista.push({ clave, respuesta: valor });
      }
    }
    this.armaDetalle();
    this.cdr.detectChanges();
  }

  private encola(
    idEval: number,
    op: () => Observable<EvalProveedorRes>,
    porOmision: string,
    alTerminar?: (bien: boolean, res?: EvalProveedorRes) => void,
  ): void {
    this.pendientes.push({ idEval, op, porOmision, alTerminar });
    if (!this.enCurso) {
      this.siguiente();
    }
  }

  private siguiente(): void {
    const t = this.pendientes.shift();
    if (!t) {
      this.enCurso = false;
      return;
    }
    this.enCurso = true;
    const id = t.idEval;
    // Si mientras tanto se pidió un detalle directo (abrir, reintentar,
    // firmar), el expediente que traiga esta operación puede ser más viejo
    // que ese: entonces no se pinta tal cual, se vuelve a leer.
    const base = this.seq;
    t.op().subscribe({
      next: (res) => {
        if (res.codigoError || !res.evaluacion) {
          this.fallaEnFila(t, res);
          return;
        }
        if (t.alTerminar) {
          t.alTerminar(true, res);
        }
        const ev = res.evaluacion;
        if (this.idSel !== ev.idEvalProveedor) {
          // Ya se ve otro proveedor: sólo se pone al día su fila en la tabla.
          this.actualizaFila(ev);
          this.cdr.detectChanges();
        } else if (!this.pendientesDe(id)) {
          if (this.seq === base) {
            ++this.seq;
            this.aplica(ev, false);
          } else {
            this.cargarDetalle(id, undefined, false);
          }
        }
        this.siguiente();
      },
      error: () => this.fallaEnFila(t, null),
    });
  }

  /**
   * Falló una operación de la fila. Se tiran sólo las que venían detrás del
   * MISMO proveedor (se decidieron viendo algo que no se guardó) y, si es el
   * que se ve, se vuelve a leer. Las de otro proveedor siguen su curso.
   */
  private fallaEnFila(t: Pendiente, res: EvalProveedorRes | null): void {
    const id = t.idEval;
    const texto = res ? this.mensaje(res, t.porOmision) : "Sin conexión con el servidor: no se guardó el último cambio.";
    const tiradas = this.descartaPendientes(id);
    if (t.alTerminar) {
      t.alTerminar(false, res || undefined);
    }
    if (this.idSel === id) {
      this.saliendo = false;
      this.aviso(tiradas ? texto + " Tampoco se guardó lo que venía detrás." : texto, "error");
      this.cargarDetalle(id, undefined, false);
    } else {
      const e = this.evaluaciones.find((x) => x.idEvalProveedor === id);
      this.aviso((e && e.nombre ? e.nombre + ": " : "") + texto, "error");
    }
    this.siguiente();
  }

  /** Quita de la fila las operaciones de un proveedor (o todas) y les avisa que no se hicieron. */
  private descartaPendientes(idEval?: number): number {
    const tiradas = this.pendientes.filter((x) => idEval == null || x.idEval === idEval);
    this.pendientes = this.pendientes.filter((x) => !(idEval == null || x.idEval === idEval));
    for (const x of tiradas) {
      if (x.alTerminar) {
        x.alTerminar(false);
      }
    }
    return tiradas.length;
  }

  private pendientesDe(idEval: number): boolean {
    return this.pendientes.some((x) => x.idEval === idEval);
  }

  /**
   * Vuelve a leer el expediente POR LA FILA, detrás de lo que ya iba: así lo
   * último que se pinta es posterior a todas las escrituras conocidas (una
   * subida, un archivo quitado) y no pisa respuestas que siguen en camino.
   */
  private refrescaEnFila(id: number, alTerminar?: (bien: boolean, res?: EvalProveedorRes) => void): void {
    if (this.idSel !== id) {
      return;
    }
    this.encola(id, () => this.service.detalle(id), "No se pudo volver a leer el expediente.", alTerminar);
  }

  editarObs(p: PreguntaVista): void {
    if (this.firmada) {
      return;
    }
    p.editando = true;
    p.borrador = p.observacion;
    this.cdr.detectChanges();
  }

  cancelarObs(p: PreguntaVista): void {
    p.editando = false;
    p.borrador = p.observacion;
  }

  guardarObs(p: PreguntaVista): void {
    const ev = this.sel;
    if (!ev || this.firmada || p.guardando) {
      return;
    }
    const texto = p.borrador.trim();
    if (texto === p.observacion.trim()) {
      p.editando = false;
      return;
    }
    const id = ev.idEvalProveedor as number;
    const clave = p.c.clave;
    // Se escribe ya en el expediente que se ve, como las respuestas: si la
    // pantalla se rehace antes de que vuelva (otro clic), no enseña la
    // observación vieja. Si no se guarda, se regresa a la anterior.
    const lista = ev.respuestas || (ev.respuestas = []);
    let renglon = lista.find((x) => x.clave === clave);
    if (!renglon) {
      renglon = { clave, respuesta: null, observacion: null };
      lista.push(renglon);
    }
    const fila = renglon;
    const anterior = fila.observacion || null;
    fila.observacion = texto || null;
    p.guardando = true;
    // En la misma fila que las respuestas: si no, una respuesta contestada
    // antes llegaba después con la observación vieja y la pintaba encima.
    this.encola(
      id,
      () => this.service.observar(id, clave, texto),
      "No se guardó la observación.",
      (bien) => {
        // Las preguntas se rehacen con cada respuesta: se toca también la que
        // se ve ahora (por su clave), no sólo la que se tenía en la mano.
        const vistas = [p, ...(this.idSel === id ? this.preguntas.filter((q) => q !== p && q.c.clave === clave) : [])];
        if (!bien && fila.observacion === (texto || null)) {
          fila.observacion = anterior;
        }
        for (const q of vistas) {
          q.guardando = false;
          if (bien) {
            q.observacion = texto;
            q.borrador = texto;
            q.editando = false;
          } else {
            // El editor se queda abierto con lo escrito, para volver a guardar.
            q.observacion = anterior || "";
          }
        }
        this.cdr.detectChanges();
      },
    );
  }

  /* ---------------- Expediente ---------------- */

  guardarReferencia(d: DocVista): void {
    const ev = this.sel;
    if (!ev || this.firmada) {
      return;
    }
    const id = ev.idEvalProveedor as number;
    const clave = d.cat.clave;
    const llave = id + "|" + clave;
    const texto = d.referencia.trim();
    const doc = (ev.documentos || []).find((x) => x.claveDoc === clave);
    if (texto === ((doc && doc.referencia) || "")) {
      d.sucia = false;
      if (this.refsSinGuardar.delete(llave)) {
        d.sinGuardar = false;
      }
      return;
    }
    // Se escribe ya en el expediente: la próxima comparación es contra lo
    // último que se mandó, y un repintado no regresa el texto viejo. Si no se
    // guarda, se regresa a lo anterior y la tarjeta lo avisa.
    const anterior = doc ? doc.referencia || null : null;
    if (doc) {
      doc.referencia = texto || null;
    }
    this.refsSinGuardar.delete(llave);
    d.sinGuardar = false;
    // También en la fila, por lo mismo que las observaciones. El expediente
    // que vuelve pone al día el aviso de "falta respaldo".
    this.encola(
      id,
      () => this.service.referencia(id, clave, texto),
      "No se guardó dónde consta el documento.",
      (bien) => {
        if (!bien) {
          if (doc && (doc.referencia || "") === texto) {
            doc.referencia = anterior;
          }
          this.refsSinGuardar.add(llave);
          for (const x of [d, ...(this.idSel === id ? this.docs.filter((y) => y !== d && y.cat.clave === clave) : [])]) {
            x.sinGuardar = true;
          }
          this.cdr.detectChanges();
          return;
        }
        if (this.idSel !== id) {
          return;
        }
        // Se limpia la marca sólo si no se escribió otra cosa mientras tanto.
        for (const x of [d, ...this.docs.filter((y) => y !== d && y.cat.clave === clave)]) {
          if (x.referencia.trim() === texto) {
            x.sucia = false;
          }
        }
      },
    );
  }

  esEnlace(ref: string): boolean {
    return /^https?:\/\/\S+$/i.test((ref || "").trim());
  }

  subir(d: DocVista, evento: Event): void {
    const input = evento.target as HTMLInputElement;
    const archivo = input.files && input.files[0];
    input.value = "";
    const ev = this.sel;
    const idDoc = d.idEvalDoc;
    if (!archivo || !ev || this.firmada || !idDoc || this.subiendoDocs.has(idDoc)) {
      return;
    }
    const id = ev.idEvalProveedor as number;
    this.marcaSubiendo(idDoc, true);
    this.evidencias.subirEvidencia(archivo, ORIGEN_EVAL_PROVEEDOR_DOC, idDoc, d.cat.nombre).subscribe({
      next: (res) => {
        this.marcaSubiendo(idDoc, false);
        if (!res || res.codigoError) {
          this.saliendo = false;
          this.aviso((res && res.mensajeError) || "No se pudo subir el archivo.", "error");
          return;
        }
        this.aviso("Archivo adjunto.", "success");
        this.refrescaEnFila(id);
      },
      error: () => {
        this.marcaSubiendo(idDoc, false);
        this.saliendo = false;
        this.aviso("No se pudo subir el archivo: sin conexión con el servidor.", "error");
      },
    });
  }

  /**
   * "Subiendo…" va por id de documento y no sólo en la tarjeta: las
   * tarjetas se rehacen con cada respuesta, y la marca se quedaba pegada en
   * una tarjeta nueva mientras se apagaba en la vieja, que ya no se veía.
   */
  private marcaSubiendo(idDoc: number, sube: boolean): void {
    if (sube) {
      this.subiendoDocs.add(idDoc);
    } else {
      this.subiendoDocs.delete(idDoc);
    }
    for (const x of this.docs) {
      if (x.idEvalDoc === idDoc) {
        x.subiendo = sube;
      }
    }
    this.cdr.detectChanges();
  }

  verArchivo(a: MatrizEvidencia): void {
    if (!a.idEvidencia) {
      return;
    }
    this.cierraVistazo(true);
    const nombre = a.nombreArchivo || "evidencia";
    this.evidencias.descargarEvidencia(a.idEvidencia).subscribe({
      next: (datos) => {
        // Sólo PDF e imágenes se abren en una pestaña; lo demás se baja. Un
        // blob abierto en pestaña corre con el origen de la app, y un tipo
        // declarado a mano (text/html) podía ejecutar código con la sesión.
        const mime = (a.mimeType || "").toLowerCase();
        const enLinea = /^(application\/pdf|image\/(png|jpeg|gif|webp|bmp))$/.test(mime);
        const blob = new Blob([datos], { type: enLinea ? mime : "application/octet-stream" });
        const url = URL.createObjectURL(blob);
        if (enLinea) {
          window.open(url, "_blank");
        } else {
          const liga = document.createElement("a");
          liga.href = url;
          liga.download = nombre;
          liga.click();
        }
        setTimeout(() => URL.revokeObjectURL(url), 30000);
      },
      error: () => this.aviso("No se pudo abrir el archivo.", "error"),
    });
  }

  quitarArchivo(d: DocVista, a: MatrizEvidencia): void {
    const ev = this.sel;
    if (!ev || this.firmada || !a.idEvidencia) {
      return;
    }
    const id = ev.idEvalProveedor as number;
    const idEvidencia = a.idEvidencia;
    Swal.fire({
      title: "¿Quitar el archivo?",
      text: `«${a.nombreArchivo}» de ${d.cat.nombre}. Queda anotado en la bitácora.`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Quitar",
      cancelButtonText: "Cancelar",
      confirmButtonColor: "#b3261e",
    }).then((r) => {
      if (!r.isConfirmed) {
        return;
      }
      this.olvidaVistazo(idEvidencia);
      this.quitando++;
      this.evidencias.eliminarEvidencia(idEvidencia).subscribe({
        next: (res) => {
          if (res && res.codigoError) {
            this.saliendo = false;
            this.aviso(res.mensajeError || "No se pudo quitar el archivo.", "error");
          }
          // El almacén de evidencias contesta vacío cuando algo falla: se
          // comprueba contra lo que de verdad quedó, leyendo por la fila.
          this.refrescaEnFila(id, (bien, leido) => {
            const sigue =
              !!leido &&
              !!leido.evaluacion &&
              (leido.evaluacion.documentos || []).some((x) => (x.archivos || []).some((y) => y.idEvidencia === idEvidencia));
            if (bien && sigue && !(res && res.codigoError)) {
              this.saliendo = false;
              this.aviso("No se pudo quitar el archivo.", "error");
            }
          });
          // Hasta aquí: el refresco ya va en la fila, que también cuenta.
          this.quitando--;
        },
        error: () => {
          // Cuando el servidor lo rechaza, el almacén contesta con error HTTP:
          // se vuelve a leer para enseñar lo que de verdad quedó.
          this.saliendo = false;
          this.aviso("No se pudo quitar el archivo.", "error");
          this.refrescaEnFila(id);
          this.quitando--;
        },
      });
    });
  }

  /* ---------------- Vista previa de la evidencia ---------------- */

  /**
   * Abre el tooltip con la vista previa. La primera vez espera un poco,
   * para que no salte con sólo cruzar el ratón; si acaba de verse otra,
   * abre al instante, que es justo cuando se revisan varias seguidas.
   */
  abreVistazo(a: MatrizEvidencia, evento: Event): void {
    const el = evento.currentTarget as HTMLElement | null;
    if (!el || !a.idEvidencia) {
      return;
    }
    this.cancelaCierre();
    if (this.relojVistazo) {
      clearTimeout(this.relojVistazo);
    }
    const seguido = !!this.vistazo || Date.now() - this.cerradoEn < 500;
    this.relojVistazo = setTimeout(() => this.muestraVistazo(a, el), seguido ? 0 : 280);
  }

  /** Cierra con un respiro, para poder pasar el ratón del nombre al tooltip y usar su botón. */
  cierraVistazo(inmediato = false): void {
    if (this.relojVistazo) {
      clearTimeout(this.relojVistazo);
      this.relojVistazo = null;
    }
    this.cancelaCierre();
    const cierra = () => {
      this.relojCierre = null;
      if (this.vistazo) {
        this.vistazo = null;
        this.cerradoEn = Date.now();
        this.cdr.detectChanges();
      }
    };
    if (inmediato) {
      cierra();
    } else {
      this.relojCierre = setTimeout(cierra, 160);
    }
  }

  cancelaCierre(): void {
    if (this.relojCierre) {
      clearTimeout(this.relojCierre);
      this.relojCierre = null;
    }
  }

  @HostListener("document:keydown.escape")
  alEscape(): void {
    if (this.vistazo) {
      this.cierraVistazo(true);
    } else if (this.edicion) {
      this.cerrarEdicion(true);
    }
  }

  /** Si la página se desplaza, el tooltip quedaría flotando lejos de su archivo. */
  private readonly alDesplazar = (): void => {
    if (this.vistazo) {
      this.cierraVistazo(true);
    }
  };

  private muestraVistazo(a: MatrizEvidencia, el: HTMLElement): void {
    this.relojVistazo = null;
    if (!el.isConnected) {
      return;
    }
    const tipo = this.tipoVistazo(a);
    const r = el.getBoundingClientRect();
    const ancho = Math.min(360, window.innerWidth - 32);
    const alto = tipo === "otro" ? 150 : 360;
    // Abajo del nombre; si no cabe, arriba; si tampoco, pegado al borde de la ventana.
    const cabeAbajo = window.innerHeight - r.bottom - 8 >= alto;
    const arriba = !cabeAbajo && r.top - 8 >= alto;
    const grande = (a.tamanioBytes || 0) > this.MAX_VISTAZO;
    this.vistazo = {
      a,
      tipo,
      estado: tipo === "otro" ? "sin" : grande ? "grande" : "cargando",
      urlImagen: null,
      urlPdf: null,
      meta: this.metaArchivo(a),
      x: Math.round(Math.max(16, Math.min(r.left, window.innerWidth - ancho - 16))),
      y: Math.round(arriba ? r.top - 8 : cabeAbajo ? r.bottom + 8 : Math.max(16, window.innerHeight - alto - 16)),
      arriba,
      ancho,
    };
    this.cdr.detectChanges();
    if (tipo !== "otro" && !grande) {
      this.cargaVistazo(a, tipo);
    }
  }

  private cargaVistazo(a: MatrizEvidencia, tipo: "imagen" | "pdf"): void {
    const id = a.idEvidencia as number;
    const listo = this.blobsVistazo.get(id);
    if (listo) {
      this.ponVistazo(id, tipo, listo);
      return;
    }
    this.evidencias.descargarEvidencia(id).subscribe({
      next: (datos) => {
        // El tipo lo pone la pantalla por la extensión, igual que el
        // servidor: nunca uno que el navegador pueda ejecutar.
        const mime = tipo === "pdf" ? "application/pdf" : this.mimeImagen(a);
        const url = URL.createObjectURL(new Blob([datos], { type: mime }));
        this.blobsVistazo.set(id, url);
        this.ponVistazo(id, tipo, url);
      },
      error: () => {
        if (this.vistazo && this.vistazo.a.idEvidencia === id) {
          this.vistazo.estado = "error";
          this.cdr.detectChanges();
        }
      },
    });
  }

  private ponVistazo(id: number, tipo: "imagen" | "pdf", url: string): void {
    const v = this.vistazo;
    if (!v || v.a.idEvidencia !== id) {
      return;
    }
    // Es un blob que armó la propia pantalla, con tipo PDF o imagen forzado.
    if (tipo === "pdf") {
      v.urlPdf = this.sanitizer.bypassSecurityTrustResourceUrl(url + "#toolbar=0&navpanes=0&view=FitH");
    } else {
      v.urlImagen = this.sanitizer.bypassSecurityTrustUrl(url);
    }
    v.estado = "listo";
    this.cdr.detectChanges();
  }

  /** El archivo se quita: fuera también su vista previa guardada. */
  private olvidaVistazo(idEvidencia: number): void {
    const url = this.blobsVistazo.get(idEvidencia);
    if (url) {
      URL.revokeObjectURL(url);
      this.blobsVistazo.delete(idEvidencia);
    }
    if (this.vistazo && this.vistazo.a.idEvidencia === idEvidencia) {
      this.cierraVistazo(true);
    }
  }

  private tipoVistazo(a: MatrizEvidencia): "imagen" | "pdf" | "otro" {
    const ext = extension(a.nombreArchivo);
    if (["png", "jpg", "jpeg", "gif", "webp", "bmp"].includes(ext)) {
      return "imagen";
    }
    return ext === "pdf" ? "pdf" : "otro";
  }

  private mimeImagen(a: MatrizEvidencia): string {
    const ext = extension(a.nombreArchivo);
    return ext === "jpg" ? "image/jpeg" : `image/${ext}`;
  }

  /** "PNG · 245 KB · Norman Estrada · 14/09/2026 15:32" */
  private metaArchivo(a: MatrizEvidencia): string {
    const partes: string[] = [];
    const ext = extension(a.nombreArchivo);
    if (ext) {
      partes.push(ext.toUpperCase());
    }
    if (a.tamanioBytes != null) {
      partes.push(tamanoLegible(a.tamanioBytes));
    }
    if (a.usuarioNombre) {
      partes.push(a.usuarioNombre.replace(/\s+/g, " ").trim());
    }
    const cuando = fechaHora(a.fechaCarga);
    if (cuando) {
      partes.push(cuando);
    }
    return partes.join(" · ");
  }

  /* ======================= 3 · Resultado ======================= */

  elegirDictamen(d: DictamenEval): void {
    const r = this.res;
    if (this.firmada || !r) {
      return;
    }
    if (d === "APROBADO" && !r.puedeAprobar) {
      return;
    }
    this.dictamenElegido = d;
    this.dictamenManual = true;
  }

  /** Cerrar y firmar: las mismas revisiones de antes y, luego, la firma digital. */
  firmar(): void {
    const ev = this.sel;
    const r = this.res;
    const dictamen = this.dictamenElegido;
    if (!ev || !r || !dictamen || this.firmada || this.firmando) {
      return;
    }
    // Con algo guardándose, el resultado que se ve todavía no es el del
    // servidor: se firmaría con respuestas viejas.
    if (this.ocupado) {
      this.aviso("Espera a que se guarde lo último y vuelve a firmar.", "warning");
      return;
    }
    // Se firmaria con los datos guardados, no con los que se ven.
    if (this.datosSinGuardar) {
      this.aviso("Los datos del proveedor no se han guardado (paso 2 · Cuestionario): se vuelven a intentar.", "warning");
      this.guardarDatos();
      return;
    }
    if (this.faltanDatos.length) {
      this.aviso("Faltan los datos del proveedor: " + this.faltanDatos.join(", ") + " (paso 2 · Cuestionario).", "warning");
      return;
    }
    if (dictamen === "APROBADO" && this.sinRespaldo.length) {
      this.aviso("Para aprobar falta subir la evidencia de: " + this.sinRespaldo.join(", ") + ".", "warning");
      return;
    }
    this.firmaPara = { id: ev.idEvalProveedor as number, dictamen };
    this.firmaLeyenda =
      `Dictamen: ${DICTAMEN_TEXTO[dictamen]} · puntaje ${r.respondidos ? texto1(r.total) : "—"} · riesgo ${RIESGO_TEXTO[r.riesgo]}. ` +
      "Tu firma cierra la evaluación: firmada ya no se puede cambiar; para corregirla habrá que reabrirla y dejar el motivo.";
    this.firmaAbierta = true;
    this.cdr.detectChanges();
  }

  cierraFirma(): void {
    this.firmaAbierta = false;
    this.firmaPara = null;
    this.cdr.detectChanges();
  }

  /** Llegó el trazo: se firma, y al terminar en Resultado se regresa a Proveedores. */
  alFirmar(firma: string): void {
    const para = this.firmaPara;
    this.firmaAbierta = false;
    this.firmaPara = null;
    if (!para || this.idSel !== para.id || this.firmada || this.firmando) {
      this.cdr.detectChanges();
      return;
    }
    const id = para.id;
    const dictamen = para.dictamen;
    // Lo que se firma tiene que seguir siendo lo que decía el modal.
    const r = this.res;
    if (
      this.ocupado ||
      this.datosSinGuardar ||
      this.dictamenElegido !== dictamen ||
      !r ||
      this.faltanDatos.length > 0 ||
      (dictamen === "APROBADO" && (!r.puedeAprobar || this.sinRespaldo.length > 0))
    ) {
      this.aviso("La evaluación cambió mientras firmabas: revisa el resultado y vuelve a firmar.", "warning");
      this.cdr.detectChanges();
      return;
    }
    this.firmando = true;
    this.cdr.detectChanges();
    this.service.firmar(id, dictamen, firma).subscribe({
      next: (res) => {
        this.firmando = false;
        if (res.codigoError || !res.evaluacion) {
          this.aviso(this.mensaje(res, "No se pudo firmar la evaluación."), "error");
          this.cdr.detectChanges();
          return;
        }
        this.actualizaFila(res.evaluacion);
        this.aviso(`Evaluación firmada como ${DICTAMEN_TEXTO[dictamen]}.`, "success");
        if (this.idSel === id) {
          this.volverALista();
        } else {
          this.cdr.detectChanges();
        }
      },
      error: () => {
        this.firmando = false;
        this.aviso("No se pudo firmar: sin conexión con el servidor.", "error");
        this.cdr.detectChanges();
      },
    });
  }

  reabrir(): void {
    const ev = this.sel;
    if (!ev || !this.firmada || this.reabriendo) {
      return;
    }
    const id = ev.idEvalProveedor as number;
    Swal.fire({
      title: "Reabrir la evaluación",
      text: "Se quita la firma y el dictamen; las respuestas y los archivos se quedan.",
      input: "textarea",
      inputLabel: "¿Por qué se reabre? Queda en la bitácora.",
      inputPlaceholder: "Ej. El proveedor publicó su certificación ISO/IEC 42001",
      inputAttributes: { maxlength: "700" },
      showCancelButton: true,
      confirmButtonText: "Reabrir",
      cancelButtonText: "Cancelar",
      confirmButtonColor: "#0e7a6d",
      inputValidator: (v) => (!v || !v.trim() ? "Escribe el motivo." : null),
    }).then((x) => {
      if (!x.isConfirmed || this.idSel !== id) {
        return;
      }
      this.reabriendo = true;
      this.cdr.detectChanges();
      this.service.reabrir(id, String(x.value).trim()).subscribe({
        next: (res) => {
          this.reabriendo = false;
          if (res.codigoError || !res.evaluacion) {
            this.aviso(this.mensaje(res, "No se pudo reabrir la evaluación."), "error");
            this.cdr.detectChanges();
            return;
          }
          this.aviso("Evaluación reabierta.", "success");
          if (this.idSel !== id) {
            this.actualizaFila(res.evaluacion);
            this.cdr.detectChanges();
            return;
          }
          ++this.seq;
          this.dictamenManual = false;
          this.aplica(res.evaluacion, true);
        },
        error: () => {
          this.reabriendo = false;
          this.aviso("No se pudo reabrir: sin conexión con el servidor.", "error");
          this.cdr.detectChanges();
        },
      });
    });
  }

  /* ======================= Editar desde la tabla ======================= */

  /**
   * Los datos del proveedor con su bitácora de cambios. En una firmada sólo
   * se consulta: los campos van apagados y la bitácora se ve igual.
   */
  editar(f: FilaProveedor): void {
    const ev = this.evaluaciones.find((e) => e.idEvalProveedor === f.id);
    if (!ev) {
      return;
    }
    this.edicion = {
      id: f.id,
      nombre: ev.nombre || "",
      firmada: f.firmada,
      datos: {
        nombre: ev.nombre || "",
        casoUso: ev.casoUso || "",
        razonSocial: ev.razonSocial || "",
        servicio: ev.servicio || "",
        planModalidad: ev.planModalidad || "",
        fechaEvaluacion: ev.fechaEvaluacion || "",
      },
    };
    // Siempre se abre en Datos: es a lo que se entra, y la bitácora está a un
    // clic. Sin reiniciar, el modal recordaría la pestaña del proveedor anterior
    // y el siguiente se abriría en una bitácora que todavía no es la suya.
    this.tabEdicion = "datos";
    this.diceEdicion("");
    this.historial = [];
    this.historialError = "";
    this.edicionDesde = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    this.cargarHistorial(f.id);
    this.cdr.detectChanges();
    // En una firmada los campos van apagados: el foco entra por el ✕.
    setTimeout(() => this.host.nativeElement.querySelector<HTMLElement>(f.firmada ? ".eia-modal__cerrar" : "#eia-editar-nombre")?.focus());
  }

  /**
   * @param suave Escape, el fondo o el ✕: con cambios sin guardar no cierra,
   *   avisa. El botón Cancelar cierra siempre.
   */
  cerrarEdicion(suave = false): void {
    if (this.guardandoEdicion) {
      return;
    }
    if (suave && this.edicion && !this.edicion.firmada && !this.edicionSinCambios) {
      this.diceEdicion("Hay cambios sin guardar: pulsa Guardar cambios o Cancelar.", "aviso");
      return;
    }
    this.edicion = null;
    this.diceEdicion("");
    this.historial = [];
    this.historialError = "";
    this.cdr.detectChanges();
    const volverA = this.edicionDesde;
    this.edicionDesde = null;
    if (volverA && volverA.isConnected) {
      volverA.focus();
    }
  }

  /** Tab no sale del cuadro: da la vuelta entre lo que se puede enfocar dentro. */
  atrapaFoco(evento: Event): void {
    const caja = this.host.nativeElement.querySelector<HTMLElement>(".eia-modal__caja");
    if (!caja) {
      return;
    }
    const focables = Array.from(
      caja.querySelectorAll<HTMLElement>("input:not([disabled]), button:not([disabled]), [tabindex]:not([tabindex='-1'])"),
    ).filter((el) => el.offsetParent !== null);
    if (!focables.length) {
      evento.preventDefault();
      return;
    }
    const primero = focables[0];
    const ultimo = focables[focables.length - 1];
    const atras = (evento as KeyboardEvent).shiftKey;
    const activo = document.activeElement;
    if (atras && (activo === primero || !caja.contains(activo))) {
      evento.preventDefault();
      ultimo.focus();
    } else if (!atras && (activo === ultimo || !caja.contains(activo))) {
      evento.preventDefault();
      primero.focus();
    }
  }

  private diceEdicion(texto: string, tono: "error" | "aviso" | "ok" = "error", borraEn?: number): void {
    if (this.relojEdicion) {
      clearTimeout(this.relojEdicion);
      this.relojEdicion = null;
    }
    this.edicionError = texto;
    this.edicionTono = tono;
    if (borraEn) {
      this.relojEdicion = setTimeout(() => {
        this.edicionError = "";
        this.cdr.detectChanges();
      }, borraEn);
    }
  }

  get historialEstado(): string {
    if (this.historialError) {
      return this.historialError;
    }
    if (this.cargandoHistorial && !this.historial.length) {
      return "Cargando…";
    }
    const n = this.historial.length;
    return n ? `${n} ${n === 1 ? "línea" : "líneas"}` : "Sin movimientos";
  }

  /** Lo que se mandaría, ya recortado. */
  private datosEdicion(): Edicion["datos"] | null {
    const e = this.edicion;
    if (!e) {
      return null;
    }
    return {
      nombre: e.datos.nombre.trim(),
      casoUso: e.datos.casoUso.trim(),
      razonSocial: e.datos.razonSocial.trim(),
      servicio: e.datos.servicio.trim(),
      planModalidad: e.datos.planModalidad.trim(),
      fechaEvaluacion: e.datos.fechaEvaluacion || "",
    };
  }

  get edicionSinCambios(): boolean {
    const e = this.edicion;
    const d = this.datosEdicion();
    const ev = e && this.evaluaciones.find((x) => x.idEvalProveedor === e.id);
    if (!e || !d || !ev) {
      return true;
    }
    return (
      d.nombre === (ev.nombre || "") &&
      d.casoUso === (ev.casoUso || "") &&
      d.razonSocial === (ev.razonSocial || "") &&
      d.servicio === (ev.servicio || "") &&
      d.planModalidad === (ev.planModalidad || "") &&
      d.fechaEvaluacion === (ev.fechaEvaluacion || "")
    );
  }

  guardarEdicion(): void {
    const e = this.edicion;
    const d = this.datosEdicion();
    if (!e || !d || e.firmada || this.guardandoEdicion || this.edicionSinCambios) {
      return;
    }
    if (!d.nombre) {
      this.diceEdicion("Falta el nombre del proveedor o la herramienta.");
      return;
    }
    if (!d.casoUso) {
      this.diceEdicion("Falta el caso de uso previsto.");
      return;
    }
    this.guardandoEdicion = true;
    this.diceEdicion("");
    this.service.guardarDatos(e.id, d).subscribe({
      next: (res) => {
        this.guardandoEdicion = false;
        if (res.codigoError) {
          this.diceEdicion(this.mensaje(res, "No se guardaron los cambios."));
          this.cdr.detectChanges();
          return;
        }
        const actual = this.evaluaciones.find((x) => x.idEvalProveedor === e.id);
        for (const obj of [actual, this.sel && this.sel.idEvalProveedor === e.id ? this.sel : null]) {
          if (obj) {
            obj.nombre = d.nombre;
            obj.casoUso = d.casoUso;
            obj.razonSocial = d.razonSocial;
            obj.servicio = d.servicio;
            obj.planModalidad = d.planModalidad;
            obj.fechaEvaluacion = d.fechaEvaluacion;
          }
        }
        e.nombre = d.nombre;
        this.armaLista();
        // Dentro del cuadro, no en toast: el toast quedaría detrás del cuadro.
        this.diceEdicion("Guardado; quedó en la bitácora.", "ok", 3000);
        // La línea nueva de la bitácora, a la vista.
        this.cargarHistorial(e.id);
        this.cdr.detectChanges();
      },
      error: () => {
        this.guardandoEdicion = false;
        this.diceEdicion("No se guardaron los cambios: sin conexión con el servidor.");
        this.cdr.detectChanges();
      },
    });
  }

  /** La bitácora del cuadro de Editar. Lo que llega de otro expediente (ya cerrado) se ignora. */
  private cargarHistorial(id: number): void {
    this.cargandoHistorial = true;
    this.historialError = "";
    const mio = () => !!this.edicion && this.edicion.id === id;
    this.service.historial(id).subscribe({
      next: (res) => {
        if (!mio()) {
          return;
        }
        this.cargandoHistorial = false;
        if (res.codigoError) {
          this.historialError = this.mensaje(res, "No se pudo cargar la bitácora.");
        } else {
          this.historial = res.historial || [];
        }
        this.cdr.detectChanges();
      },
      error: () => {
        if (!mio()) {
          return;
        }
        this.cargandoHistorial = false;
        this.historialError = "No se pudo cargar la bitácora.";
        this.cdr.detectChanges();
      },
    });
  }

  /* ======================= Apoyos de la plantilla ======================= */

  colorDom(id: string): string {
    return this.DOM_COLOR[id] || "#68756f";
  }

  accionTexto(accion: string): string {
    return ACCION_TEXTO[accion] || accion;
  }

  nivelScore(score: number | null): string {
    if (score == null) {
      return "nulo";
    }
    return score >= 80 ? "ok" : score >= 60 ? "warn" : "mal";
  }

  /* ======================= Privados ======================= */

  private criterio(clave: string): EvalCriterio | undefined {
    for (const d of this.catalogo ? this.catalogo.dominios : []) {
      const c = d.criterios.find((x) => x.clave === clave);
      if (c) {
        return c;
      }
    }
    return undefined;
  }

  /** "Pregunta 2 · Privacidad y uso de nuestros datos". */
  private etiquetaCriterio(clave: string): string {
    for (const d of this.catalogo ? this.catalogo.dominios : []) {
      const c = d.criterios.find((x) => x.clave === clave);
      if (c) {
        return `Pregunta ${c.numero} · ${d.nombre}`;
      }
    }
    return clave;
  }

  /** El color de un proveedor sale de su lugar en la tabla, como en el prototipo. */
  private colorDe(id: number): string {
    let i = this.evaluaciones.findIndex((e) => e.idEvalProveedor === id);
    if (i < 0) {
      i = Math.abs(id);
    }
    return this.COLORES[i % this.COLORES.length];
  }

  private mensaje(res: EvalProveedorRes, porOmision: string): string {
    // AUTH-001 viaja en un 200 con codigoError: ningún interceptor lo ve.
    if (res && res.codigoError === "AUTH-001") {
      return "Tu sesión caducó. Vuelve a entrar.";
    }
    return (res && res.mensajeError) || porOmision;
  }

  private mensajePin(res: PinAutorizadorRes, porOmision: string): string {
    if (res.codigoError === "AUTH-001") {
      return "Tu sesión caducó. Vuelve a entrar.";
    }
    return res.mensajeError || porOmision;
  }

  private aviso(texto: string, icono: "success" | "error" | "warning" | "info"): void {
    Swal.fire({
      toast: true,
      position: "top-end",
      icon: icono,
      title: texto,
      showConfirmButton: false,
      timer: icono === "error" ? 6000 : 2500,
      timerProgressBar: true,
    });
  }

  private subeAlInicio(): void {
    setTimeout(() => {
      const h = this.host.nativeElement as HTMLElement;
      h.scrollIntoView({ block: "start" });
      // Si el botón que se pulsó se fue con la vista (Terminar, Ver
      // resultado…), el foco caía en <body>: pasa a la pestaña activa, que
      // es la misma barra en las tres vistas y dice dónde se está.
      const activo = document.activeElement;
      const tab = h.querySelector(".eia-tab--activa") as HTMLElement | null;
      if (tab && (!activo || activo === document.body || !activo.isConnected)) {
        tab.focus({ preventScroll: true });
      }
      // En angosto la tira de pestañas no cabe: se recorre hasta la activa.
      const nav = tab?.parentElement;
      if (tab && nav) {
        const a = tab.getBoundingClientRect();
        const b = nav.getBoundingClientRect();
        if (a.right > b.right) {
          nav.scrollLeft += a.right - b.right;
        } else if (a.left < b.left) {
          nav.scrollLeft -= b.left - a.left;
        }
      }
    });
  }

  /**
   * «Siguiente →» entre secciones. En escritorio sube al inicio (las
   * preguntas van a la derecha del índice). En una sola columna (≤900px)
   * el índice va arriba y ocupa casi toda la pantalla: ahí se baja al
   * panel de preguntas, justo debajo de la barra fija.
   */
  private subeAlPanel(): void {
    if (typeof window.matchMedia !== "function" || !window.matchMedia("(max-width: 900px)").matches) {
      this.subeAlInicio();
      return;
    }
    setTimeout(() => {
      const h = this.host.nativeElement as HTMLElement;
      const panel = h.querySelector(".eia-panel") as HTMLElement | null;
      const cab = h.querySelector(".eia-cabecera--fija") as HTMLElement | null;
      if (!panel) {
        return;
      }
      // El tope es lo más bajo entre la barra de COS y la del módulo, y
      // nunca negativo: en el teléfono la del módulo no se pega (body con
      // overflow-x hidden) y, ya bajada, su fondo queda muy arriba.
      const nav = document.querySelector(".navbar-solser-shell") as HTMLElement | null;
      const tope = Math.max(nav ? nav.getBoundingClientRect().bottom : 0, cab ? cab.getBoundingClientRect().bottom : 0, 0);
      panel.style.scrollMarginTop = Math.round(tope + 8) + "px";
      panel.scrollIntoView({ block: "start" });
    });
  }

  /** Las tipografías del diseño, una sola vez; si no cargan, queda la del sistema. */
  private cargaFuentes(): void {
    if (document.getElementById(FUENTES_ID)) {
      return;
    }
    const link = document.createElement("link");
    link.id = FUENTES_ID;
    link.rel = "stylesheet";
    link.href = FUENTES_URL;
    document.head.appendChild(link);
  }

  /** Dónde se quedó quien mira (pestaña, proveedor y dominio). Sólo comodidad. */
  private guardaLocal(): void {
    try {
      localStorage.setItem(LLAVE_LOCAL, JSON.stringify({ vista: this.vista, idSel: this.idSel, dom: this.dom }));
    } catch {
      // Sin almacenamiento local la pantalla funciona igual.
    }
  }

  private leeLocal(): { vista?: Vista; idSel?: number; dom?: string } | null {
    try {
      const t = localStorage.getItem(LLAVE_LOCAL);
      return t ? JSON.parse(t) : null;
    } catch {
      return null;
    }
  }
}
